import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaUsers, FaEye } from 'react-icons/fa';
import { API_BASE } from '../utils/helpers';

const User = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [coinFilter, setCoinFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const usersPerPage = 10;

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/user`, {
        params: { search: searchTerm, filter: coinFilter, page: currentPage, limit: usersPerPage },
      });
      setUsers(res.data.users || []);
      setTotalPages(res.data.pagination?.totalPages || 1);
      setError('');
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, coinFilter, currentPage]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const address = (u) =>
    [u.address1, u.address2, u.city, u.province, u.zip, u.country].filter(Boolean).join(', ');

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900">Users</h2>
        <p className="text-sm text-slate-500">App customers synced from Shopify.</p>
      </div>

      {/* Filters */}
      <div className="ui-card mb-5 p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="relative sm:col-span-2">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400" />
            <input
              className="ui-input pl-9"
              type="text"
              placeholder="Search by name, email or Shopify ID…"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <select
            className="ui-input"
            value={coinFilter}
            onChange={(e) => { setCoinFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="all">All points</option>
            <option value="zero">0 points</option>
            <option value="positive">&gt; 0 points</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="ui-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="ui-table">
            <thead>
              <tr>
                <th className="w-12">#</th>
                <th>Shopify ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Points</th>
                <th>Created</th>
                <th>Address</th>
                <th className="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading &&
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    {Array.from({ length: 8 }).map((__, j) => (
                      <td key={j}><div className="h-4 w-full max-w-[120px] animate-pulse rounded bg-slate-100" /></td>
                    ))}
                  </tr>
                ))}

              {!loading && error && (
                <tr><td colSpan={8} className="py-10 text-center text-rose-600">{error}</td></tr>
              )}

              {!loading && !error && users.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <div className="flex flex-col items-center justify-center gap-2 py-12 text-slate-400">
                      <FaUsers className="text-2xl" />
                      <p className="text-sm">No users found.</p>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && !error && users.map((user, index) => (
                <tr key={user.id}>
                  <td className="text-slate-400">{(currentPage - 1) * usersPerPage + index + 1}</td>
                  <td className="font-mono text-xs text-slate-500">{user.shopify_id}</td>
                  <td className="font-medium text-slate-900">{user.name || '—'}</td>
                  <td className="text-slate-500">{user.email || '—'}</td>
                  <td>{user.points ?? 0}</td>
                  <td className="whitespace-nowrap text-slate-500">
                    {user.shopify_created_at ? new Date(user.shopify_created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="max-w-[240px] truncate text-slate-500" title={address(user)}>
                    {address(user) || '—'}
                  </td>
                  <td className="text-right">
                    {user.shopify_id && (
                      <button
                        className="ui-btn-ghost px-2.5 py-1.5 text-xs"
                        onClick={() => navigate(`/admin/users/${user.shopify_id}`)}
                      >
                        <FaEye className="text-xs" /> View
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-center gap-1 border-t border-slate-200 px-4 py-3">
            {[...Array(totalPages).keys()].map((num) => {
              const page = num + 1;
              const active = page === currentPage;
              return (
                <button
                  key={page}
                  onClick={() => page !== currentPage && setCurrentPage(page)}
                  className={`h-8 min-w-8 rounded-lg px-2 text-sm font-medium transition-colors ${
                    active ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {page}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default User;
